/**
 * "Ordem da revelação" na barra do FRAME — registro aditivo, zero arquivo do upstream.
 *
 * `custom:affine:surface:frame` é o slot que `resolveToolbarActions`
 * (`widgets/toolbar/src/utils.ts:227`) combina com o módulo do upstream, então o
 * `frame-toolbar.ts` deles fica intocado.
 *
 * ═══ Por que `editor-menu-button`, e não `createButtonPopper` ══════════════════════════════
 *
 * A primeira versão montava um componente próprio com `createButtonPopper`, copiando o idioma
 * do `present/frame-order-button.ts`. **Em campo, no iPad do operador, a lista não abria** — e
 * a causa não é o popper, é o CONTÊINER.
 *
 * `EditorMenuButton` passa `topLayer: true` (`components/src/toolbar/menu-button.ts:57`), o que
 * faz o popper usar a **Popover API nativa** (`showPopover()` + `position: fixed`) e renderizar
 * na *top layer* do navegador — fora de todo clipping e de todo contexto de empilhamento. O
 * default é `topLayer: false`, ou seja `position: absolute` **dentro do shadow DOM da barra**,
 * sujeito a qualquer ancestral que corte ou cubra.
 *
 * O `frame-order-button` do upstream pode usar o default porque vive na **barra de
 * apresentação**, que é um elemento fixo de topo. Eu copiei o idioma de um contêiner onde ele
 * funciona para outro onde não funciona — e a prova de que o mecanismo certo é este está no
 * próprio relato: o menu **⋮ da mesma barra** abre no aparelho dele, e é `editor-menu-button`.
 *
 * A troca ainda apaga um arquivo: o componente de botão deixou de existir.
 */

import { FrameBlockModel } from '@blocksuite/affine-model';
import {
  type ToolbarContext,
  type ToolbarModuleConfig,
  ToolbarModuleExtension,
} from '@blocksuite/affine-shared/services';
import { LayerIcon } from '@blocksuite/icons/lit';
import { BlockFlavourIdentifier } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import type { ExtensionType } from '@blocksuite/store';
import { html } from 'lit';

import { passosDoFrame } from './marcacao';

const ROTULO = 'Ordem da revelação';

const configuracao = {
  actions: [
    {
      id: 'z.tuninho-ordem-da-revelacao',
      /*
       * Só com UM quadro selecionado, e só se ele TIVER passo.
       *
       * A ordem é por quadro — dois selecionados não têm lista comum. E um botão que abre uma
       * lista vazia em todo quadro seria ruído permanente na barra: quem nunca usou a função
       * não deveria pagar por ela. O caminho de descoberta é o outro — marcar o primeiro
       * elemento pelo ⋮ dele.
       */
      when: (ctx: ToolbarContext) => {
        const frames = ctx.getSurfaceModelsByType(FrameBlockModel);
        return frames.length === 1 && passosDoFrame(frames[0]).length > 0;
      },
      /*
       * ⚠️ **`content`, e NÃO `render`.**
       *
       * A chave que devolve template é `content` (`toolbar-service/action.ts:27`), e é a que o
       * seletor de cor do próprio frame usa (`frame-toolbar.ts:145`). `render` não existe no
       * tipo — e chave desconhecida em objeto literal **não falha**: é ignorada, e a ação nunca
       * desenha. Medido no `dev` em 2026-08-29.
       */
      content: (ctx: ToolbarContext) => {
        const frame = ctx.getCurrentModelByType(FrameBlockModel);
        if (!frame) return null;

        const edgeless = ctx.std.get(GfxControllerIdentifier).surfaceComponent;
        if (!edgeless) return null;

        return html`
          <editor-menu-button
            aria-label=${ROTULO}
            .contentPadding=${'8px'}
            .button=${html`
              <editor-icon-button aria-label=${ROTULO} .tooltip=${ROTULO}>
                ${LayerIcon()}
              </editor-icon-button>
            `}
          >
            <tuninho-menu-de-passos
              .edgeless=${edgeless}
              .frame=${frame}
            ></tuninho-menu-de-passos>
          </editor-menu-button>
        `;
      },
    },
  ],
  when: (ctx: ToolbarContext) =>
    ctx.getSurfaceModelsByType(FrameBlockModel).length > 0,
} as const satisfies ToolbarModuleConfig;

export const acaoDeOrdemDosPassos: ExtensionType = ToolbarModuleExtension({
  id: BlockFlavourIdentifier('custom:affine:surface:frame'),
  config: configuracao,
});
